import { useLCS } from '@works-web/settings/modules/nds';
import { PageContent } from '@works-web/settings/modules/page';
import { useToast } from '@works-web/settings/modules/toast';
import UnauthorizedScene from '@works-web/settings/scenes/unauthorized';
import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Tooltip,
  Toggle,
  TimeSetting,
  Dialog,
  DialogButton,
} from '~/settings/__modules__/component';
import {
  AutoReplyBiz,
  DEFAULT_TIME,
  TIME_ZONE,
} from '~/settings/biz/AutoReplyBiz';
import { AppData } from '~/settings/service/CommonService';

const AREA_OF_SHOW_HOLIDAY = [
  'Asia/Seoul',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Urumqi',
  'Asia/Harbin',
  'Asia/Kashgar',
  'Asia/Chongqing',
];

const ifShowHoliday = AREA_OF_SHOW_HOLIDAY.includes(TIME_ZONE);

AutoReplySub.propTypes = {
  isExternal: PropTypes.bool,
};

export default function AutoReplySub({ isExternal }) {
  useLCS('pcset_message_autoreply');
  const { t } = useTranslation();
  const toast = useToast();

  const WEEK_DAYS_DES = {
    '0123456': t('talk_reply_daily'),
    12345: t('talk_reply_weekdays'),
    '06': t('talk_reply_weekend'),
    0: t('talk_sun'),
    1: t('talk_mon'),
    2: t('talk_tue'),
    3: t('talk_wed'),
    4: t('talk_thu'),
    5: t('talk_fri'),
    6: t('talk_sat'),
  };

  const supportExternalRoom = AppData.DomainConfig.supportExternalRoom;

  const [config, setConfig] = useState(null);
  const [alertMsg, setAlertMsg] = useState(null);
  const [curTimeOption, setCurTimeOption] = useState(null);
  const [timezoneOffset] = useState(AutoReplyBiz.getGMTOffset());
  const [isChange, setIsChange] = useState(false);

  const createDaysText = (days) => {
    const weekdays = [...days];
    const weekdaysStr = weekdays.join('');
    if (WEEK_DAYS_DES[weekdaysStr]) {
      return WEEK_DAYS_DES[weekdaysStr];
    } else {
      if (weekdays.length > 1 && weekdays[0] === 0) {
        // Sun sholud be shown at the end
        weekdays.push(weekdays.splice(0, 1)[0]);
      }
      let strArr = [];
      for (let i = 0, len = weekdays.length; i < len; i++) {
        strArr[i] = WEEK_DAYS_DES[weekdays[i]];
      }
      return strArr.join(', ');
    }
  };

  const saveEnableImmediately = () => {
    // enable执为false立即保存
    const config_ = { ...config, enable: false };
    AutoReplyBiz.save(config_, isExternal)
      .then((success) => {
        if (success) {
          toast(t('saved'));
          setConfig(config_);
          setIsChange(false);
        } else {
          setIsChange(true);
        }
      })
      .catch(() => {
        setAlertMsg({
          title: <Trans i18nKey="error_unknown_title" />,
          description: <Trans i18nKey="error_unknown_description" />,
        });
        setIsChange(true);
      });
  };

  const handleChange = (key, value) => {
    if (key === 'enable' && value === false) {
      // 及时保存
      saveEnableImmediately();
      return;
    }
    if (key === 'content' && value.length > 300) {
      value = value.substr(0, 300);
    }
    const param = { ...config, [key]: value };
    setConfig(param);
    setIsChange(AutoReplyBiz.checkIsChange(param));
  };

  const handleTimeChange = (timeOption) => {
    let timeSettings = [...config.timeSettings];
    for (let i = 0, len = timeSettings.length; i < len; i++) {
      const timeItem = timeSettings[i];
      if (
        timeItem.startTime === timeOption.startTime &&
        timeItem.endTime === timeOption.endTime &&
        timeItem.weekdays.join('') === timeOption.weekdays.join('')
      ) {
        setAlertMsg({
          title: t('talk_reply_alert6'),
        });
        return;
      }
    }
    setCurTimeOption(null);

    let index = timeSettings.findIndex((item) => item.uuid === timeOption.uuid);
    if (index > -1) {
      // 编辑
      timeSettings[index] = timeOption;
    } else {
      // 新增
      timeOption.uuid = AutoReplyBiz.uuid();
      timeSettings.push(timeOption);
    }

    const param = {
      ...config,
      timeSettings,
    };
    setConfig(param);
    setIsChange(AutoReplyBiz.checkIsChange(param));
  };

  const editTime = (timeOption) => {
    setCurTimeOption(timeOption);
  };

  const deleteTime = (timeOption) => {
    let timeSettings = [...config.timeSettings];
    if (timeSettings.length === 1) {
      setAlertMsg({
        title: t('talk_reply_alert5'),
      });
      return;
    }
    let index = timeSettings.findIndex((item) => item.uuid === timeOption.uuid);
    timeSettings.splice(index, 1);
    const param = {
      ...config,
      timeSettings,
    };
    setConfig(param);
    setIsChange(AutoReplyBiz.checkIsChange(param));
  };

  const save = () => {
    if (!config.content || !config.content.trim()) {
      setAlertMsg({
        title: t('talk_reply_alert3'),
      });
      return;
    }
    AutoReplyBiz.save(config, isExternal)
      .then((success) => {
        if (success) {
          toast(t('saved'));
          setIsChange(false);
        } else {
          // toast('保存失败');
          setIsChange(true);
        }
      })
      .catch(() => {
        setAlertMsg({
          title: <Trans i18nKey="error_unknown_title" />,
          description: <Trans i18nKey="error_unknown_description" />,
        });
        setIsChange(true);
      });
  };

  useEffect(() => {
    let result = AutoReplyBiz.init(isExternal);
    setConfig(result);
  }, [isExternal]);

  if (isExternal && !supportExternalRoom) {
    return <UnauthorizedScene />;
  }

  return (
    <PageContent
      title={
        isExternal
          ? t('talk_main_replytitle')
          : t('talk_main_replytitle_internal')
      }
      parentURL="/settings"
      confirmOnLeave={isChange}
    >
      {config && (
        <>
          <ul className="detail_settings_list external_message">
            <li className="item has_toggle">
              <h4 className="title">
                {isExternal
                  ? t('talk_main_replytitle')
                  : t('talk_main_replytitle_internal')}
              </h4>
              <div className="option_area">
                <Toggle
                  value={config.enable}
                  label={'Toggle use of auto-reply to external messages'}
                  onChange={() => handleChange('enable', !config.enable)}
                />
              </div>
            </li>
            {config.enable && (
              <>
                <li className="item auto_reply">
                  <h4 className="title">
                    <span className="text">{t('talk_reply_time')}</span>
                    <span className="timezone">{timezoneOffset}</span>
                  </h4>
                  <button
                    type="button"
                    className="lw_btn"
                    onClick={() => setCurTimeOption(DEFAULT_TIME)}
                  >
                    {t('talk_reply_addtime')}
                  </button>
                  <div className="lw_table_cover">
                    <table className="lw_table lw_table_head tb_auto_reply">
                      <thead>
                        <tr>
                          <th scope="col" className="days">
                            {t('talk_reply_week')}
                          </th>
                          <th scope="col" className="time">
                            {t('talk_reply_timerange')}
                          </th>
                          <th scope="col" className="manage">
                            {t('manage')}
                          </th>
                        </tr>
                      </thead>
                    </table>
                    <div className="lw_table_scroll">
                      <table className="lw_table tb_auto_reply">
                        <tbody>
                          {config.timeSettings &&
                            config.timeSettings.map((timeOption) => {
                              return (
                                <tr key={timeOption.uuid}>
                                  <td className="days">
                                    {createDaysText(timeOption.weekdays)}
                                  </td>
                                  <td className="time">
                                    {timeOption.startTime === '00:00' &&
                                    timeOption.endTime === '23:59'
                                      ? t('talk_reply_allday')
                                      : `${timeOption.startTime} ~ ${timeOption.endTime}`}
                                  </td>
                                  <td className="manage">
                                    <div className="task">
                                      <button
                                        type="button"
                                        onClick={() => editTime(timeOption)}
                                      >
                                        {t('talk_edit')}
                                      </button>
                                      <span className="bar"></span>
                                      <button
                                        type="button"
                                        onClick={() => deleteTime(timeOption)}
                                      >
                                        {t('button_delete')}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {ifShowHoliday && (
                    <>
                      <h4 className="title">
                        <span className="text">
                          {t('talk_reply_holidaynoti')}
                        </span>
                        <Tooltip>{t('talk_reply_holidaynoti_tooltip')}</Tooltip>
                      </h4>
                      <div className="option_area">
                        <Toggle
                          value={config.enableHoliday}
                          label={'Toggle off notifications on holidays'}
                          onChange={() =>
                            handleChange('enableHoliday', !config.enableHoliday)
                          }
                        />
                      </div>
                    </>
                  )}
                </li>
                <li className="item cols">
                  <h4 className="title">{t('talk_reply_message')}</h4>
                  <div className="textarea_cover">
                    <div className="counter">
                      <span className="text">
                        <em className="point">{config.content?.length || 0}</em>
                        /300
                      </span>
                    </div>
                    <textarea
                      name="text"
                      cols="30"
                      rows="4"
                      placeholder={t('talk_reply_guide1')}
                      value={config.content}
                      onChange={(e) => handleChange('content', e.target.value)}
                      maxLength={300}
                    ></textarea>
                  </div>
                </li>
              </>
            )}
          </ul>
          {config.enable && (
            <div className="btn_area">
              <button
                type="button"
                className="lw_btn_point_40"
                onClick={save}
                disabled={isChange ? false : true}
              >
                {t('button_save')}
              </button>
            </div>
          )}
        </>
      )}
      {alertMsg && (
        <Dialog.Alert
          title={alertMsg.title}
          description={alertMsg.description}
          onClose={() => setAlertMsg(null)}
        >
          <DialogButton.Action onSelect={(close) => close()}>
            {t('icon_confirm')}
          </DialogButton.Action>
        </Dialog.Alert>
      )}
      {curTimeOption && (
        <TimeSetting
          onCancle={() => setCurTimeOption(null)}
          onOk={(timeOption) => handleTimeChange(timeOption)}
          defaultValue={curTimeOption}
        />
      )}
    </PageContent>
  );
}
